import PublicLayout from "@/components/layout/PublicLayout";
import { Link } from "react-router-dom";
import { ArrowRight, Shield, Lock, Users, KeyRound, CreditCard, EyeOff, Activity, AlertTriangle } from "lucide-react";

const LegalHero = ({ eyebrow, title }) => (
  <section className="bg-white py-20 border-b border-[var(--ft-border)]">
    <div className="ft-container max-w-3xl">
      <div className="ft-eyebrow mb-5">{eyebrow}</div>
      <span className="ft-gold-rule-lg" />
      <h1 className="mt-6 text-[clamp(2.25rem,4.5vw,3.5rem)] font-bold leading-[1.02] tracking-tight text-[var(--ft-black)]">{title}</h1>
      <div className="mt-6 text-sm text-[var(--ft-muted)]">Effective {new Date().toLocaleDateString("en-US", { month: "long", year: "numeric" })}</div>
    </div>
  </section>
);

const Section = ({ title, children }) => (
  <div className="mb-10">
    <h2 className="text-xl font-semibold mb-2 text-[var(--ft-black)]">{title}</h2>
    <span className="ft-gold-rule" />
    <div className="text-[var(--ft-charcoal)] leading-relaxed space-y-3 mt-4 text-[15px]">{children}</div>
  </div>
);

// -----------------------------------------------------------------
export const Privacy = () => (
  <PublicLayout>
    <LegalHero eyebrow="Legal" title="Privacy Notice" />
    <section className="py-16">
      <div className="ft-container max-w-3xl">
        <Section title="Information We Collect">
          <p>FLOWTALIS™ collects information you provide directly when booking consultations, submitting contact forms, creating client accounts, communicating with our team, or engaging with our platform. This may include your name, company, email address, phone number, project specifications, and any files you upload (such as artwork, brand assets, or contracts).</p>
          <p>We also automatically collect basic analytics such as pages visited, device type, and interaction events so we can measure engagement and improve the platform.</p>
        </Section>
        <Section title="How Information Is Used">
          <p>Your information is used to deliver FLOWTALIS services — including product development, manufacturing coordination, artwork production, invoicing, shipping, customer success, and ongoing account management. We also use it to send you project updates, communicate about your account, and respond to inquiries.</p>
        </Section>
        <Section title="Cookies">
          <p>We use a minimal set of cookies and local storage to keep you signed in to the client portal, remember preferences, and gather aggregate usage analytics. You can control cookies through your browser settings; disabling them may limit certain functionality of the portal.</p>
        </Section>
        <Section title="Third-Party Services">
          <p>We work with select third-party services for hosting, payment processing, email delivery, analytics, and AI assistance. Each provider is chosen for its security posture. Information is shared only to the extent required to deliver the service and never sold.</p>
        </Section>
        <Section title="Marketing Communications">
          <p>If you opt in, we may send occasional updates about FLOWTALIS products, launches, and events. You can unsubscribe at any time using the link in any email.</p>
        </Section>
        <Section title="Data Protection">
          <p>All data is stored in access-controlled infrastructure with encryption at rest and in transit. Documents, contracts, and artwork are stored in secure object storage with role-based access controls.</p>
        </Section>
        <Section title="User Rights">
          <p>You may request access to, correction of, or deletion of your personal information at any time by emailing hello@flowtalis.com. We will respond within a reasonable time frame consistent with applicable law.</p>
        </Section>
        <Section title="Children's Privacy">
          <p>FLOWTALIS services are intended for business use and are not directed to children under the age of 13. We do not knowingly collect personal information from children.</p>
        </Section>
        <Section title="Contact Information">
          <p>Questions about this policy? Contact us at <a href="mailto:hello@flowtalis.com" className="font-semibold" style={{ color: "var(--ft-gold)" }}>hello@flowtalis.com</a>.</p>
        </Section>
        <Section title="Updates to This Privacy Notice">
          <p>We may update this Privacy Notice from time to time. Material changes will be communicated through the platform or by email. The date at the top of this page reflects the most recent revision.</p>
        </Section>
      </div>
    </section>
  </PublicLayout>
);

// -----------------------------------------------------------------
export const Terms = () => (
  <PublicLayout>
    <LegalHero eyebrow="Legal" title="Terms & Conditions" />
    <section className="py-16">
      <div className="ft-container max-w-3xl">
        <Section title="Acceptance of Terms">
          <p>By accessing the FLOWTALIS™ website or engaging with our services, you agree to be bound by these Terms &amp; Conditions. If you do not agree, please discontinue use of the platform.</p>
        </Section>
        <Section title="Services Offered">
          <p>FLOWTALIS provides private-label beverage manufacturing, product development, brand and artwork design, packaging, logistics, and related professional services. Specific deliverables, timelines, MOQs, and pricing are defined per client engagement.</p>
        </Section>
        <Section title="Client Responsibilities">
          <p>Clients are responsible for providing accurate brand assets, timely approvals, and payment according to agreed schedules. Delays in client-provided approvals or materials may extend production timelines.</p>
        </Section>
        <Section title="Quotes &amp; Pricing">
          <p>Quotes are valid for 30 days from issuance unless otherwise stated. Pricing is subject to change based on raw material costs, MOQ adjustments, or specification changes requested after quote acceptance.</p>
        </Section>
        <Section title="Payment Terms">
          <p>Standard payment terms require a deposit upon proposal acceptance and remaining balance according to milestones defined in the engagement. All amounts are stated in USD unless otherwise agreed.</p>
        </Section>
        <Section title="Intellectual Property">
          <p>Client brand assets, artwork, and product specifications remain the property of the client. FLOWTALIS retains all rights to its platform, workflows, internal methodologies, templates, and design systems.</p>
        </Section>
        <Section title="Confidentiality">
          <p>Both parties agree to hold confidential all non-public information exchanged during an engagement, including formulations, pricing, artwork, and strategic plans.</p>
        </Section>
        <Section title="Manufacturing Disclaimer">
          <p>While FLOWTALIS partners exclusively with FDA-compliant facilities, beverage manufacturing involves inherent variability. FLOWTALIS does not warrant specific market performance, sales outcomes, or consumer reception of any launched product.</p>
        </Section>
        <Section title="Limitation of Liability">
          <p>To the maximum extent permitted by law, FLOWTALIS&apos; total liability under any engagement shall not exceed the fees paid by the client for the specific deliverable giving rise to the claim.</p>
        </Section>
        <Section title="Governing Law">
          <p>These terms are governed by United States law. Any disputes shall be resolved through binding arbitration under the rules of the American Arbitration Association.</p>
        </Section>
        <Section title="Termination">
          <p>Either party may terminate an engagement in writing subject to the payment and cancellation provisions of the applicable engagement agreement.</p>
        </Section>
        <Section title="Contact Information">
          <p>For questions regarding these terms, contact <a href="mailto:hello@flowtalis.com" className="font-semibold" style={{ color: "var(--ft-gold)" }}>hello@flowtalis.com</a>.</p>
        </Section>
      </div>
    </section>
  </PublicLayout>
);

// -----------------------------------------------------------------
export const Security = () => {
  const items = [
    { Icon: Lock, title: "Secure Data Storage", desc: "All client data is stored in access-controlled cloud infrastructure with encryption at rest." },
    { Icon: Shield, title: "SSL Encryption", desc: "All traffic between your browser and FLOWTALIS is encrypted using industry-standard TLS." },
    { Icon: Users, title: "Role-Based Access Controls", desc: "Access is granted on a least-privilege basis. Internal staff see only what their role requires." },
    { Icon: KeyRound, title: "Password Protection", desc: "Passwords are hashed using bcrypt. Client accounts support strong password requirements." },
    { Icon: CreditCard, title: "Secure Payment Processing", desc: "Payment processing uses PCI-compliant partners. FLOWTALIS does not store raw card details." },
    { Icon: EyeOff, title: "Restricted Internal Access", desc: "Client information is available only to team members directly assigned to the account." },
    { Icon: Activity, title: "Routine Security Monitoring", desc: "The platform is monitored for anomalies and access patterns are audited regularly." },
    { Icon: AlertTriangle, title: "Incident Response Procedures", desc: "Documented procedures ensure prompt containment, investigation, and client notification." },
  ];
  return (
    <PublicLayout>
      <LegalHero eyebrow="Legal" title="Security Policy" />
      <section className="py-16">
        <div className="ft-container max-w-3xl">
          <p className="text-[var(--ft-charcoal)] text-[15px] leading-relaxed mb-10">
            FLOWTALIS™ is committed to protecting customer information through industry best practices. Our security posture is
            reviewed regularly and applied across every part of the platform — from public marketing pages to private client portals.
          </p>
          <div className="grid md:grid-cols-2 gap-4">
            {items.map(({ Icon, title, desc }) => (
              <div key={title} className="ft-card">
                <Icon size={22} strokeWidth={1.4} style={{ color: "var(--ft-gold)" }} />
                <h3 className="mt-4 font-semibold text-[var(--ft-black)]">{title}</h3>
                <p className="mt-2 text-sm text-[var(--ft-muted)] leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
          <div className="mt-12 ft-card-flat text-[15px] leading-relaxed text-[var(--ft-charcoal)]">
            While no system can guarantee absolute security, FLOWTALIS continuously works to maintain a secure and reliable platform.
            Questions or reports? Email <a href="mailto:hello@flowtalis.com" className="font-semibold" style={{ color: "var(--ft-gold)" }}>hello@flowtalis.com</a>.
          </div>
        </div>
      </section>
    </PublicLayout>
  );
};

// -----------------------------------------------------------------
export const Refund = () => (
  <PublicLayout>
    <LegalHero eyebrow="Legal" title="Refund Policy" />
    <section className="py-16">
      <div className="ft-container max-w-3xl">
        <div className="text-[var(--ft-charcoal)] text-[15px] leading-relaxed space-y-5">
          <p>
            Due to the nature of our services — custom product development, manufacturing coordination, design work, consulting
            services, and digital deliverables — all payments made to FLOWTALIS are considered final.
          </p>
          <p>
            FLOWTALIS does not offer refunds once work has commenced or services have been rendered.
          </p>
          <p>
            Refunds will only be considered if FLOWTALIS determines that the issue resulted directly from an error, failure, or
            negligence on the part of FLOWTALIS.
          </p>
          <p>
            Any approved refund requests will be reviewed on a case-by-case basis at FLOWTALIS&apos; sole discretion.
          </p>
          <p>
            Custom manufacturing costs, design services, deposits, production expenses, and third-party supplier costs are non-refundable.
          </p>
        </div>

        <div className="mt-10 ft-card">
          <div className="ft-eyebrow mb-3">Questions?</div>
          <p className="text-sm text-[var(--ft-muted)]">
            For questions about this policy, contact <a href="mailto:hello@flowtalis.com" className="font-semibold" style={{ color: "var(--ft-gold)" }}>hello@flowtalis.com</a>.
          </p>
          <div className="mt-6">
            <Link to="/contact" className="ft-btn-gold">Contact Us <ArrowRight size={16} /></Link>
          </div>
        </div>
      </div>
    </section>
  </PublicLayout>
);
